import type { ReactElement } from "react";
import { classes } from "./classes.ts";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";
import { checkFrame, frameClass, tickClass } from "./state.ts";

/**
 * A checkbox (brief section 5.1): a box drawn in the field frame, the tick inside it drawn from the atlas as
 * original art, and a pixel-font label. The whole row takes the pointer, so a click or a tap anywhere on it
 * toggles; the native input inside the box keeps the keyboard and assistive technology, and its keyboard
 * focus is the aether outline around the box. A checkbox that ends a row whose own text says what it is for, as
 * the design draws Settings' rows, shows no label: the box alone, named by the label for assistive technology.
 */

/**
 * A checkbox.
 *
 * @param props The label, whether it is `checked`, `onChange` with the new value, `disabled`, and `labelHidden`
 *   for the box alone, the label its accessible name; and `describedBy`, the id of an element that describes it,
 *   when it has one.
 * @returns The checkbox.
 */
export function Checkbox({ label, checked, onChange, disabled = false, labelHidden = false, describedBy }: { readonly label: string; readonly checked: boolean; readonly onChange: (checked: boolean) => void; readonly disabled?: boolean; readonly labelHidden?: boolean; readonly describedBy?: string | undefined }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const metrics = KIT_METRICS.check;
  return (
    <label
      className={classes("ot-check", state.hover && "is-hover", state.pressed && "is-pressed", disabled && "is-disabled", checked && "is-selected")}
      data-kit="checkbox"
      style={{ height: logical(metrics.row), gap: logical(labelHidden ? 0 : metrics.gap) }}
      onPointerEnter={handlers.onPointerEnter}
      onPointerLeave={handlers.onPointerLeave}
      onPointerDown={handlers.onPointerDown}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
    >
      <span className={classes("ot-check__box", frameClass(checkFrame({ ...state, disabled })), state.focus && "is-focus")} data-kit="checkbox-box" style={{ width: logical(metrics.box), height: logical(metrics.box), marginTop: logical(metrics.boxOffset) }}>
        <input className="ot-check__input" type="checkbox" aria-label={labelHidden ? label : undefined} aria-describedby={describedBy} checked={checked} disabled={disabled} onChange={(event) => onChange(event.currentTarget.checked)} onFocus={handlers.onFocus} onBlur={handlers.onBlur} onKeyDown={handlers.onKeyDown} onKeyUp={handlers.onKeyUp} />
        {checked ? <span className={classes("ot-check__tick", tickClass(disabled))} aria-hidden="true" /> : null}
      </span>
      {labelHidden ? null : (
        <span className="ot-check__label" style={{ marginTop: logical(metrics.labelOffset) }}>
          {label}
        </span>
      )}
    </label>
  );
}
